import { AppChannel, Channel } from "@/config/channel"
import { isRecaptchaError } from "@/hooks/use-recaptcha"
import { FetcherErrorResponse } from "@/services/create-fetcher"
import { InfoMessageList } from "@/services/get-messages"
import {
  ContactsToVerify,
  ContractSettings,
  LoginErrorResponse,
  MultiAction,
  SecuritySetting,
  SsoLoginResponse,
  VerificationEmailStateEnum,
  VerificationPhoneNumberStateEnum,
} from "@/services/post-login-sso"
import { SsoLoginV3Response } from "@/services/post-login-sso-v3"

export function computePostLoginAction<
  T extends SsoLoginV3Response = SsoLoginV3Response,
>(response: T, channel: Channel, messages: InfoMessageList): MultiAction {
  const {
    isLoginBlocked,
    isSecurityQuestionRequired,
    passwordExpirationDays,
    flagAdvPlus,
    contactsToVerify,
    contractSettings,
    securitySettings,
    challengeRequired,
    mfaEnabled,
  } = response

  if (shouldValidateContacts(contactsToVerify)) {
    return { type: "VERIFY_CONTACTS" }
  }

  const contractAction = getContractAction(contractSettings)
  if (contractAction) return contractAction

  if (window.APP_COUNTRY === "IT" && needsSelfLimitations(securitySettings)) {
    return { type: "MUST_ACCEPT_SELF_LIMITATIONS" }
  }

  if (isLoginBlocked) return { type: "ACCOUNT_BLOCKED" }

  if (flagAdvPlus) return { type: "QUESTIONARIO" }

  if (challengeRequired && mfaEnabled) {
    return { type: "MFA_REQUIRED" }
  }

  const channelsWithPasswordExpiringFlow: Set<Channel> = new Set([
    AppChannel.PortaleWebFullResponsiveWwwSisalIt,
    AppChannel.ClientDesktopSisalPoker,
  ])

  const expiringPassword = isPasswordExpiring(passwordExpirationDays)

  if (
    !isSecurityQuestionRequired &&
    messages.length === 0 &&
    (!channelsWithPasswordExpiringFlow.has(channel) || !expiringPassword)
  ) {
    return { type: "URL_REDIRECT" }
  }

  return {
    type:
      channelsWithPasswordExpiringFlow.has(channel) && expiringPassword
        ? "PASSWORD_EXPIRING"
        : "NOTHING",
    showListModals: messages.length > 0,
  }
}

function shouldValidateContacts(contactsToVerify?: ContactsToVerify) {
  if (!contactsToVerify) return false

  const { verificationEmailState, verificationPhoneNumberState } =
    contactsToVerify

  const shouldValidatePhoneNumber =
    verificationPhoneNumberState ===
      VerificationPhoneNumberStateEnum.VALIDATE_PHONE_NUMBER_15_DAYS ||
    verificationPhoneNumberState ===
      VerificationPhoneNumberStateEnum.VALIDATE_PHONE_NUMBER_31_DAYS

  const shouldValidateEmail =
    verificationEmailState ===
      VerificationEmailStateEnum.VALIDATE_EMAIL_15_DAYS ||
    verificationEmailState === VerificationEmailStateEnum.VALIDATE_EMAIL_31_DAYS

  return shouldValidatePhoneNumber || shouldValidateEmail
}

function getContractAction(
  contractSettings:
    | (typeof ContractSettings)[keyof typeof ContractSettings]
    | undefined,
): MultiAction | null {
  if (contractSettings !== ContractSettings.NotAccepted) return null

  switch (window.APP_COUNTRY) {
    case "ES":
    case "PT":
    case "FR":
      return { type: "MUST_ACCEPT_NEW_CONTRACT_FOR_PLATFORM_MIGRATION" }
    default:
      return { type: "MUST_ACCEPT_NEW_CONTRACT_AND_SELF_LIMITATIONS" }
  }
}

function needsSelfLimitations(
  securitySettings:
    | (typeof SecuritySetting)[keyof typeof SecuritySetting]
    | undefined,
): boolean {
  return (
    securitySettings === SecuritySetting.NoSettingActivated ||
    securitySettings === SecuritySetting.OnlySecurityQuestions ||
    securitySettings === SecuritySetting.OnlyAutoexclusion ||
    securitySettings === SecuritySetting.SecurityQuestionsAndAutoexclusion
  )
}

export function computeHasOther<T extends SsoLoginResponse = SsoLoginResponse>(
  loginResponse: T,
  messages: InfoMessageList,
) {
  const { passwordExpirationDays, isSecurityQuestionRequired } = loginResponse
  const hasOther =
    isPasswordExpiring(passwordExpirationDays) ||
    isSecurityQuestionRequired ||
    messages.length > 0
  return hasOther
}

export function computeErrorType(
  response: FetcherErrorResponse<LoginErrorResponse>,
) {
  if (response.status !== 422) return "UNKNOWN"

  const sogeiError =
    response.data.error?.errorInfos?.generic?.some(isSogeiError) ?? false
  if (sogeiError) return "SOGEI"

  const message = response.data.error?.message

  if (isRecaptchaError(response.data)) return "ERROR_RECAPTCHA"

  if (message === "text.noAutoLimitationSet") return "NO-AUTO-LIMITATION-SET"
  if (message === "text.contractNotAccepted") return "CONTRACT-NOT-ACCEPTED"

  if (message !== "") return "CREDENTIALS"

  return "UNKNOWN"
}

function isPasswordExpiring(passwordExpirationDays: number) {
  return 0 < passwordExpirationDays && passwordExpirationDays < 8
}

function isSogeiError(error: string) {
  return error === "err.e8001" || error === "err.e8012"
}
